/**
 * Intent-Based Route Prefetching
 * Prefetches routes on hover (desktop) and after idle (mobile)
 */

import { useEffect, useCallback, useRef } from 'react';

// Routes that should be prefetched based on popularity
const PRIORITY_ROUTES = [
  { path: '/products', loader: () => import('../pages/Products') },
  { path: '/about', loader: () => import('../pages/About') },
  { path: '/pricing', loader: () => import('../pages/PricingPage') },
  { path: '/contact', loader: () => import('../pages/Contact') },
  { path: '/blog', loader: () => import('../pages/Blog') },
  { path: '/faq', loader: () => import('../pages/FAQ') },
  { path: '/ai-discovery', loader: () => import('../pages/AIDiscovery') },
];

// Track which routes have been prefetched
const prefetchedRoutes = new Set<string>();
const pendingPrefetches = new Set<string>();

// Prefetch a specific route module
const prefetchRoute = async (path: string, loader: () => Promise<any>) => {
  if (prefetchedRoutes.has(path) || pendingPrefetches.has(path)) return;
  
  pendingPrefetches.add(path);
  
  try {
    await loader();
    prefetchedRoutes.add(path);
  } catch (error) {
    // Silent fail - prefetch is optional
  } finally {
    pendingPrefetches.delete(path);
  }
};

// Prefetch all priority routes during idle time (mobile optimization)
export const prefetchPriorityRoutes = () => {
  const schedule = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 10000 });
    } else {
      setTimeout(callback, 2000);
    }
  };
  
  // Stagger prefetches to avoid blocking
  PRIORITY_ROUTES.forEach((route, index) => {
    schedule(() => {
      setTimeout(() => {
        prefetchRoute(route.path, route.loader);
      }, index * 500); // 500ms between each prefetch
    });
  });
};

// Find route loader for a given path
const findRouteLoader = (href: string): (() => Promise<any>) | null => {
  const pathname = new URL(href, window.location.origin).pathname;
  
  // Check priority routes
  const priorityRoute = PRIORITY_ROUTES.find(r => pathname.startsWith(r.path));
  if (priorityRoute) return priorityRoute.loader;
  
  // Dynamic route matching
  if (pathname.startsWith('/products/')) {
    return () => import('../components/products/ProductPageTemplate');
  }
  if (pathname.startsWith('/blog/')) {
    return () => import('../pages/BlogPost');
  }
  if (pathname.startsWith('/workspace/')) {
    return () => import('../pages/ClientWorkspace');
  }
  
  return null;
};

/**
 * Hook to enable intent-based prefetching on links
 * Prefetches on hover (desktop) or touch start (mobile)
 */
export const useIntentPrefetch = () => {
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  const handleLinkHover = useCallback((event: Event) => {
    const link = event.currentTarget as HTMLAnchorElement;
    const href = link.href;
    
    if (!href || !href.startsWith(window.location.origin)) return;
    
    const loader = findRouteLoader(href);
    if (!loader) return;
    
    // Delay prefetch slightly to avoid prefetching on accidental hovers
    hoverTimeoutRef.current = setTimeout(() => {
      prefetchRoute(new URL(href).pathname, loader);
    }, isMobile ? 0 : 150); // Immediate on touch, delayed on hover
  }, [isMobile]);
  
  const handleLinkLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);
  
  useEffect(() => {
    // Find all internal links
    const links = document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]');
    
    links.forEach(link => {
      if (isMobile) {
        link.addEventListener('touchstart', handleLinkHover, { passive: true });
      } else {
        link.addEventListener('mouseenter', handleLinkHover, { passive: true });
        link.addEventListener('mouseleave', handleLinkLeave, { passive: true });
      }
    });
    
    // Cleanup
    return () => {
      links.forEach(link => {
        link.removeEventListener('touchstart', handleLinkHover);
        link.removeEventListener('mouseenter', handleLinkHover);
        link.removeEventListener('mouseleave', handleLinkLeave);
      });
    };
  }, [handleLinkHover, handleLinkLeave, isMobile]);
  
  // On mobile, also prefetch after idle
  useEffect(() => {
    if (!isMobile) return;
    
    const timeout = setTimeout(() => {
      prefetchPriorityRoutes();
    }, 3000); // Wait 3s after mount before prefetching
    
    return () => clearTimeout(timeout);
  }, [isMobile]);
};

/**
 * Component wrapper for prefetch-enabled links
 */
export const useLinkPrefetch = (href: string) => {
  const prefetched = useRef(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  const onMouseEnter = useCallback(() => {
    if (prefetched.current || isMobile) return;
    
    const loader = findRouteLoader(href);
    if (loader) {
      prefetchRoute(href, loader);
      prefetched.current = true;
    }
  }, [href, isMobile]);
  
  const onTouchStart = useCallback(() => {
    if (prefetched.current || !isMobile) return;
    
    const loader = findRouteLoader(href);
    if (loader) {
      prefetchRoute(href, loader);
      prefetched.current = true;
    }
  }, [href, isMobile]);
  
  return { onMouseEnter, onTouchStart };
};

export { prefetchedRoutes, prefetchRoute };
